// Copyright (c) StrangeBee 2024

import { useLocalMessage } from '@Helpers/locale';
import { useFormatDate } from '@Hooks/useFormatDate';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { THPopConfirm } from '@Molecules/Inputs/THPopConfirm/THPopConfirm';
import THDrawer from '@Molecules/THDrawer/THDrawer';
import React, { ReactElement, useCallback, useMemo } from 'react';
import { DatabaseCheckOutput, DatabaseCheckStatsOutput } from 'thehive-sdk';
import { PlatformStatusEventList } from '../eventList';

import './PlatformDatabaseDetails.scss';

interface PlatformDatabaseDetailsProps {
    data: DatabaseCheckOutput & { thEntityName: string };
    visible: boolean;
    onVisibleChange: (visible: boolean) => void;
    eventList: PlatformStatusEventList;
    title?: string;
}

export const PlatformDatabaseDetails = ({
    data,
    visible,
    onVisibleChange,
    eventList,
    title,
}: PlatformDatabaseDetailsProps): ReactElement => {
    const i = useLocalMessage();
    const f = useFormatDate();

    const { thEntityName: entityName, ...stats } = data;

    const dataParsed = useMemo(
        () =>
            Object.entries(stats.check || {}).reduce((acc, [key, value]) => {
                if (['dedupLastDate', 'globalLastDate'].includes(key)) {
                    return {
                        ...acc,
                        [key]: value ? f(value) : null,
                    };
                }

                if (
                    [
                        'dedupRequested',
                        'globalCheckRequested',
                        'dedupIsRunning',
                        'globalCheckIsRunning',
                        'needCheck',
                    ].includes(key)
                ) {
                    return {
                        ...acc,
                        [key]: value ? i('general.yes') : i('general.no'),
                    };
                }

                return { ...acc, [key]: value };
            }, {} as Record<string, any>),
        [stats, i, f],
    );

    const Elem = useCallback(
        (key: keyof DatabaseCheckStatsOutput, action?: ReactElement) =>
            dataParsed[key] !== null &&
            dataParsed[key] !== undefined && (
                <div className="platform-database-details__item">
                    <div className="platform-database-details__key">{i(`platform.index.${key}`)}</div>
                    <div className="platform-database-details__value">
                        {dataParsed[key]} {action}
                    </div>
                </div>
            ),
        [i, dataParsed],
    );

    return (
        <THDrawer title={title || entityName} visible={visible} afterVisibleChange={onVisibleChange}>
            <div className="platform-database-details">
                {Elem('needCheck')}

                <div className="platform-database-details__title">Dedup</div>

                {Elem(
                    'dedupRequested',
                    <THPopConfirm
                        title={i('platform.check.actions.dedup.confirm', { entityName })}
                        onConfirm={() => eventList.onDedupTrigger(entityName)}
                    >
                        <THButton type="link">{i('platform.check.actions.dedup.label')}</THButton>
                    </THPopConfirm>,
                )}
                {Elem('dedupIsRunning')}
                {Elem('dedup.duplicate')}
                {Elem('dedup.duration')}
                {Elem('dedup.iteration')}
                {Elem('dedupLastDate')}
                {Elem('dedupLast.duration')}
                {Elem('dedupLast.duplicate')}
                {Elem('dedupTimer')}

                <div className="platform-database-details__title">Global</div>

                {Elem(
                    'globalCheckRequested',
                    <THPopConfirm
                        title={i('platform.check.actions.global.confirm', { entityName })}
                        onConfirm={() => eventList.onGlobalTrigger(entityName)}
                    >
                        <THButton type="link">{i('platform.check.actions.global.label')}</THButton>
                    </THPopConfirm>,
                )}
                {Elem('globalCheckIsRunning')}
                {Elem('global.duration')}
                {Elem('global.iteration')}
                {Elem('globalLastDate')}
                {Elem('globalLast.duration')}
            </div>
        </THDrawer>
    );
};
